import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSelector } from 'react-redux';
import { selectLoggedInUserId } from "../../../Store/features/user/userSlice";
import { 
	getSpotlightProductById,
	incrementShareCount,
	upvoteSpotlightProduct,
	downvoteSpotlightProduct
} from "../../../Service/user";
import ProductDetails from "./Components/Details/ProductDetails";
import Comments from "./Components/Comments/Comments";
import { FaCalendarAlt, FaComment, FaShare, FaArrowUp, FaArrowDown } from "react-icons/fa";
import Navbar from "../../../components/Navbar/Navbar";
import Navbar3 from "../../../components/Navbar/Navbar3/Navbar3";
import Footer from "../../../components/Footer/Footer";
import "./ProductDetailsPage.scss";
import { toast } from "react-hot-toast";

const PublicProductDetailsPage = () => {
	const { productId } = useParams();
	const navigate = useNavigate();
	const commentsRef = useRef(null);
	const loggedInUserId = useSelector(selectLoggedInUserId);
	
	const [commentsVisible, setCommentsVisible] = useState(true);
	const [expandedDescription, setExpandedDescription] = useState(false);
	const [commentCount, setCommentCount] = useState(0);

	const scrollToComments = () => {
		if (commentsRef.current) {
			window.scrollTo({
				top: commentsRef.current.offsetTop - 30,
				behavior: "smooth",
			});
			// Always ensure comments are visible
			setCommentsVisible(true);
		}
	};

	const [productData, setProductData] = useState({});
	const [comments, setComments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchProductData = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await getSpotlightProductById(productId);
			setProductData(response.product);
			setComments(response.comments);
			if (response.product.firstComment) {
				setCommentCount(response.comments.length + 1);
			}
			else {
				setCommentCount(response.comments.length);
			}
			
		} catch (error) {
			console.error("Error fetching product data:", error);
			setError("Failed to load product data. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (productId) {
			fetchProductData();
		}
	}, [productId]);

	// Check if user is logged in and redirect if needed
	useEffect(() => {
		if (loggedInUserId) {
			// If user is logged in, check if URL needs to be redirected
			const path = window.location.pathname;
			if (path.startsWith('/startup-spotlight-product/')) {
				// Determine if the user is an investor based on their role
				const isInvestor = JSON.parse(localStorage.getItem("loggedInUser"))?.isInvestor;
				if (isInvestor) {
					navigate(`/investor/startup-spotlight/product/${productId}`, { replace: true });
				} else {
					navigate(`/startup-spotlight/product/${productId}`, { replace: true });
				}
			}
		}
	}, [loggedInUserId, productId, navigate]);

	const handleNewComment = (newComment) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}
		setComments((prevComments) => [newComment, ...prevComments]);
	};

	const handleIncrementShareCount = async () => {
		try {
			// Only make the API call if user is logged in
			if (loggedInUserId) {
				await incrementShareCount(productId);
				// Update the product data with new share count
				setProductData(prevData => ({
					...prevData,
					shareCount: (prevData.shareCount || 0) + 1
				}));
			} else {
				// For non-logged in users, just update the UI optimistically
				setProductData(prevData => ({
					...prevData,
					shareCount: (prevData.shareCount || 0) + 1
				}));
			}
		} catch (error) {
			console.error("Error incrementing share count:", error);
		}
	};
	
	// Check if user has upvoted or downvoted
	const hasUpvoted = loggedInUserId && Array.isArray(productData?.upvotes) && productData.upvotes.includes(loggedInUserId);
	const hasDownvoted = loggedInUserId && Array.isArray(productData?.downvotes) && productData.downvotes.includes(loggedInUserId);
	
	const redirectToLogin = () => {
		navigate('/login', { state: { from: window.location.pathname, page: "public-product-details" } });
	};
	
	const handleUpvote = async () => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}
		
		try {
			const response = await upvoteSpotlightProduct(productId);
			
			if (response.status === 200) {
				// Refresh the product data completely after upvote
				await fetchProductData();
				
				// Show success message
				toast.success(response.message || "Upvoted successfully");
			}
		} catch (error) {
			console.error("Error upvoting product:", error);
			toast.error("Failed to upvote. Please try again.");
		}
	};

	const handleDownvote = async () => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}
		
		try {
			const response = await downvoteSpotlightProduct(productId);
			
			if (response.status === 200) {
				// Refresh the product data completely after downvote
				await fetchProductData();
				
				// Show success message
				toast.success(response.message || "Downvoted successfully");
			}
		} catch (error) {
			console.error("Error downvoting product:", error);
			toast.error("Failed to downvote. Please try again.");
		}
	};
	
	const goToFounderProfile = (username) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}
		navigate(`/founder/${username}`);
	};

	const toggleDescription = () => {
		setExpandedDescription(!expandedDescription);
	};

	// Format the date to display in "Month Day, Year" format
	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	return (
		<div className="public-product-details-page">
			{loggedInUserId ? <Navbar /> : <Navbar3 />}
			
			<div className="page-content">
				{loading ? (
					<div className="loading-container">
						<div className="spinner large"></div>
						<p>Loading product details...</p>
					</div>
				) : error ? (
					<div className="error-container">
						<p>{error}</p>
						<button onClick={() => window.location.reload()}>Retry</button>
					</div>
				) : (
					<div className="product-content-container">
						{/* Banner Image */}
						<div className="banner-image">
							<img src={productData.banner || "https://via.placeholder.com/1200x400"} alt={productData.title} />
						</div>

						{/* Metrics Section */}
						<div className="metrics-section">
							<div className="metric-item">
								<div className="metric-value">
									<span>{formatDate(productData.createdAt)}</span>
								</div>
								<div className="metric-label">
									<FaCalendarAlt className="metric-icon" />
									Launched On
								</div>
							</div>
							<div className="metric-item voting">
								<div className="metric-value votes-display">
									<span className="upvotes">{productData.upvotes?.length || 0}</span>
									<span className="vote-divider">|</span>
									<span className="downvotes">{productData.downvotes?.length || 0}</span>
								</div>
								<div className="metric-label">
									<div className="vote-buttons">
										<button 
											className={`upvote-button ${hasUpvoted ? 'voted' : ''}`}
											onClick={handleUpvote}
											title={hasUpvoted ? "Upvoted" : "Upvote"}
										>
											<FaArrowUp className="metric-icon" />
										</button>
										<button 
											className={`downvote-button ${hasDownvoted ? 'voted' : ''}`}
											onClick={handleDownvote}
											title={hasDownvoted ? "Downvoted" : "Downvote"}
										>
											<FaArrowDown className="metric-icon" />
										</button>
									</div>
									Votes
								</div>
							</div>
							
							<div className="metric-item">
								<div className="metric-value">{commentCount || 0}</div>
								<div className="metric-label">
									<FaComment className="metric-icon" /> Comments
								</div>
							</div>
							<div className="metric-item">
								<div className="metric-value">{productData.shareCount || 0}</div>
								<div className="metric-label">
									<FaShare className="metric-icon" /> Shares
								</div>
							</div>
						</div>

						{/* Founder Details */}
						{productData.founder && (
							<div className="founder-section">
								<h2 className="section-heading">Founder</h2>
								<div className="founder-details">
									<div className="founder-info" onClick={() => goToFounderProfile(productData.founder.userName)}>
										<img 
											src={productData.founder.profilePicture || "https://via.placeholder.com/80"} 
											alt={productData.founder.firstName + " " + productData.founder.lastName} 
											className="founder-avatar"
										/>
										<div className="founder-text">
											<h3 className="founder-name">{productData.founder.firstName + " " + productData.founder.lastName}</h3>
											<p className="founder-bio">{productData.founder.bio || "Founder"}</p>
										</div>
									</div>
									<button 
										className="view-profile-button" 
										onClick={() => goToFounderProfile(productData.founder.userName)}
									>
										View Profile
									</button>
								</div>
							</div>
						)}

						{/* Product Details Component */}
						<div className="product-details-wrapper">
							<ProductDetails
								productData={productData}
								onScrollToComments={scrollToComments}
								onIncrementShareCount={handleIncrementShareCount}
								onUpvote={handleUpvote}
								onDownvote={handleDownvote}
							/>
						</div>

						{/* Comments Section */}
						<div className="comments-wrapper">
							<motion.div
								ref={commentsRef}
								initial={{ opacity: 0, y: 20 }}
								animate={{
									opacity: commentsVisible ? 1 : 0,
									y: commentsVisible ? 0 : 20,
								}}
								transition={{ duration: 0.5 }}
								className="comments-container"
							>
								<Comments
									comments={comments}
									firstComment={productData?.firstComment}
									productId={productId}
									onCommentAdded={handleNewComment}
								/>
							</motion.div>
						</div>
					</div>
				)}
			</div>
			<Footer />
		</div>
	);
};

export default PublicProductDetailsPage;
